import { AddRounded, ArrowBack, DeleteOutlineRounded, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import locales from "../../../constants/locales";
import Loading from "../../../components/loading/loading";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { black, greyEnabled, orange, orangeLightActive, purple, purpleLight } from "../../../theme/colors";
import { cms_app_polling } from "../../../routes/routes_path";
import { createAppPolling, getAppPollingById, updateAppPolling } from "../../../services/cms/cms";
import TargetingFields, { buildTargetingPayload, defaultTargeting, targetingFromResponse, TargetingFormValues } from "../components/targeting_fields/targeting_fields";
import { AppPolling } from "../models/cms";

interface OptionFormValue {
  id?: number;
  text: string;
}

interface AppPollingFormValues {
  question: string;
  status: boolean;
  options: OptionFormValue[];
  targeting: TargetingFormValues;
}

const defaultValues: AppPollingFormValues = {
  question: "",
  status: true,
  options: [{ text: "" }, { text: "" }],
  targeting: defaultTargeting,
};

const MIN_OPTIONS = 2;

// A small bold, uppercase divider label — breaks the form into scannable
// groups instead of one long flat list of fields.
function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function AddEditAppPolling({ viewMode = false }: { viewMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useFeedback();
  const isEdit = !!id;
  // A poll is always created in English; other languages are added
  // afterwards from the edit/view screen, same convention as AMI For You.
  const [selectedLocale, setSelectedLocale] = useState("en");
  const isEnglish = selectedLocale === "en";
  // Options' existence/order is structural, only ever authored in English -
  // same rule the backend enforces (see updateAppPolling).
  const canEditOptionStructure = !isEdit || isEnglish;

  const { control, handleSubmit, watch, setValue, reset } = useForm<AppPollingFormValues>({
    defaultValues,
  });
  const { selectedOrgId } = useSelectedOrgStore();

  const { fields, append, remove } = useFieldArray({ control, name: "options" });

  const { data: itemDetails, isLoading: isItemLoading } = useQuery<AppPolling | null>(
    ["cms-app-polling-details", id, selectedLocale],
    async () => {
      const response = await getAppPollingById(id!, selectedLocale);
      return response?.data?.data;
    },
    { enabled: !!id }
  );

  useEffect(() => {
    if (itemDetails) {
      reset({
        question: itemDetails.question || "",
        status: itemDetails.status ?? true,
        options:
          itemDetails.options?.map((option) => ({ id: option.id, text: option.text || "" })) ||
          defaultValues.options,
        targeting: targetingFromResponse(itemDetails.targeting),
      });
    }
  }, [itemDetails, reset]);

  const question = watch("question");
  const options = watch("options");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit =
    !!question?.trim() &&
    question.trim().length >= 3 &&
    options?.length >= MIN_OPTIONS &&
    options.every((option) => !!option.text?.trim());

  const onSubmit = (values: AppPollingFormValues) => {
    setIsSubmitting(true);
    const payload = {
      question: values.question,
      status: values.status,
      options: values.options.map((option) => ({
        ...(option.id ? { id: option.id } : {}),
        text: option.text,
      })),
      targeting: buildTargetingPayload(values.targeting),
      ...(isEdit ? {} : { orgId: selectedOrgId || undefined }),
    };

    const request = isEdit ? updateAppPolling(id!, selectedLocale, payload) : createAppPolling(payload);
    request
      .then(() => {
        showSnackbar(isEdit ? "Poll updated successfully" : "Poll added successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-app-polling"] });
        // The list query above doesn't cover this item's own details cache
        // (keyed separately by id+locale) - without this, re-opening Edit
        // within the 5-minute default staleTime serves the stale
        // pre-update data until a hard refresh.
        queryClient.invalidateQueries({ queryKey: ["cms-app-polling-details", id] });
        navigate(cms_app_polling);
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MainLayout>
      <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
        <CardContent>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="600" ml={1}>
              {viewMode ? "Poll Results" : isEdit ? "Edit Poll" : "Add Poll"}
            </Typography>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.5, borderBottom: "1px dotted #7C797C" }} />

          {isEdit && isItemLoading ? (
            <Loading label="Loading poll..." />
          ) : (
          <>
          {!isEdit && !viewMode && (
            <Box maxWidth={360}>
              <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
            </Box>
          )}

          {isEdit && (
            <>
              <Tabs
                value={locales.findIndex((locale) => locale.code === selectedLocale)}
                onChange={(_, index: number) => setSelectedLocale(locales[index].code)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
                sx={{ mt: 2 }}
              >
                {locales.map((locale) => (
                  <Tab
                    key={locale.code}
                    label={locale.name}
                    sx={{ textTransform: "capitalize", minWidth: 90, color: greyEnabled, "&.Mui-selected": { color: purple } }}
                  />
                ))}
              </Tabs>
              <Divider />

              {itemDetails?.is_translated === false && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
                >
                  <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                  <Typography variant="body2" color={black}>
                    This poll has no content in{" "}
                    {locales.find((locale) => locale.code === selectedLocale)?.name || selectedLocale} yet — fill in
                    the details below to add this translation.
                  </Typography>
                </Box>
              )}
            </>
          )}

          <SectionHeader mt={!isEdit && !viewMode ? 3 : 2}>Question</SectionHeader>

          <Controller
            name="question"
            control={control}
            rules={{
              required: "Question is required",
              minLength: { value: 3, message: "Question must be at least 3 characters" },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                required
                label={<>Question <span style={{ color: "red" }}>*</span></>}
                margin="normal"
                disabled={viewMode}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={viewMode}
                  />
                }
                label={field.value ? "Active" : "Inactive"}
              />
            )}
          />

          <SectionHeader>
            {viewMode ? `Options${itemDetails?.total_responses !== undefined ? ` — ${itemDetails.total_responses} response${itemDetails.total_responses === 1 ? "" : "s"}` : ""}` : "Options"}
          </SectionHeader>

          {!viewMode && isEdit && !canEditOptionStructure && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
            >
              <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
              <Typography variant="body2" color={black}>
                Options can only be added, removed, or reordered while editing in English. Switch to the English tab
                to change the option list — here you can only translate existing option text.
              </Typography>
            </Box>
          )}

          <Box display="flex" flexDirection="column" gap={viewMode ? 2.5 : 1.5}>
            {fields.map((field, index) => {
              if (viewMode) {
                const option = itemDetails?.options?.[index];
                return (
                  <Box key={field.id}>
                    <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                      <Typography fontWeight={600} color={black}>
                        {option?.text || "—"}
                      </Typography>
                      <Typography variant="body2" color={greyEnabled}>
                        {option?.vote_count ?? 0} vote{(option?.vote_count ?? 0) === 1 ? "" : "s"} ({option?.vote_percentage ?? 0}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={option?.vote_percentage ?? 0}
                      sx={{ height: 10, borderRadius: 5, bgcolor: purpleLight, "& .MuiLinearProgress-bar": { bgcolor: purple, borderRadius: 5 } }}
                    />
                  </Box>
                );
              }

              return (
                <Box key={field.id} display="flex" alignItems="center" gap={1}>
                  <Controller
                    name={`options.${index}.text`}
                    control={control}
                    rules={{ required: "Option text is required" }}
                    render={({ field: f, fieldState }) => (
                      <TextField
                        {...f}
                        fullWidth
                        size="small"
                        label={`Option ${index + 1}`}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Tooltip title={fields.length <= MIN_OPTIONS ? "A poll needs at least 2 options" : "Remove option"}>
                    <span>
                      <IconButton
                        onClick={() => remove(index)}
                        disabled={fields.length <= MIN_OPTIONS || !canEditOptionStructure}
                      >
                        <DeleteOutlineRounded sx={{ color: fields.length <= MIN_OPTIONS || !canEditOptionStructure ? greyEnabled : "error.main" }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          {!viewMode && (
            <Button
              startIcon={<AddRounded />}
              onClick={() => append({ text: "" })}
              disabled={!canEditOptionStructure}
              sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, color: purple }}
            >
              Add Option
            </Button>
          )}

          <SectionHeader>Audience (More Fields)</SectionHeader>

          <TargetingFields control={control} setValue={setValue} viewMode={viewMode} />

          <Box display="flex" flexDirection="row" justifyContent="space-between" mt={5}>
            <Button onClick={() => navigate(-1)} variant="outlined" sx={{ color: black }} disabled={isSubmitting}>
              {viewMode ? "Back" : "Cancel"}
            </Button>
            {!viewMode && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !canSubmit}
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              >
                {isSubmitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update" : "Add"}
              </Button>
            )}
          </Box>
          </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
}
